/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { CloudWasteSettingKey } from "~/generated/graphql";
import { isNotNil, Maybe } from "~/tools";

type FieldPreset = {
  isApp: boolean;
  isEnv: boolean;
  isCategory: boolean;
  isPolicy: boolean;
  isService: boolean;
  isExternal: boolean;
};

type ExpandableTagData = {
  applicationsByIds?: Maybe<
    Maybe<{
      name: string;
      id: string;
      nid: string;
    }>[]
  >;
  environmentsByIds?: {
    name: string;
    id: string;
    nid: string;
    application?: {
      nid: string;
      name: string;
    };
  }[];
  categoriesByIds?: { name: string; id: string }[];
  servicesByIds?: Maybe<
    Maybe<{
      name: string;
      id: string;
    }>[]
  >;
  cloudWasteSummary?: Maybe<{
    policies?: Maybe<
      Maybe<{
        id?: CloudWasteSettingKey | null;
        title?: string | null;
      }>[]
    >;
  }>;
  externalIntegrations?: Maybe<{
    integrations?: Maybe<
      Maybe<{
        id: string;
        name: string;
      }>[]
    >;
  }>;
};

export function mapData(
  data: ExpandableTagData | undefined,
  { isApp, isEnv, isCategory, isPolicy, isService, isExternal }: FieldPreset,
  value: string[]
) {
  if (isApp) {
    return data?.applicationsByIds?.filter(isNotNil).map(({ nid, name }) => ({
      nid,
      name,
    }));
  }
  if (isEnv) {
    return data?.environmentsByIds
      ?.filter(isNotNil)
      .map(({ nid, name, application }) => ({
        nid,
        name,
        appName: application?.name,
        appNid: application?.nid,
      }));
  }
  if (isCategory) {
    return data?.categoriesByIds
      ?.filter(({ id }) => value.includes(id))
      .map(({ id, name }) => ({
        nid: id,
        name,
      }));
  }
  if (isPolicy) {
    return data?.cloudWasteSummary?.policies
      ?.filter(isNotNil)
      .filter(({ id }) => value.includes(id ?? ""))
      .map(({ id, title }) => ({
        nid: id?.toString() ?? "",
        name: title ?? "",
      }));
  }
  if (isService) {
    return data?.servicesByIds
      ?.filter(isNotNil)
      .filter(({ id }) => (id != null ? value.includes(id) : false))
      .map(({ id, name }) => ({
        nid: id?.toString() ?? "",
        name: name ?? "",
      }));
  }
  if (isExternal) {
    return data?.externalIntegrations?.integrations
      ?.filter(isNotNil)
      .filter(({ id }) => value.includes(id))
      .map(({ id, name }) => ({
        nid: id,
        name,
      }));
  }
}
